import { defineInvokeEventa } from '@moeru/eventa'
import { maxLength, maxValue, minLength, minValue, number, object, optional, pipe, string } from 'valibot'

/** Kokoro multilingual package used by the Stage 4A Chinese baseline. */
export const sherpaKokoroModelId = 'kokoro-multi-lang-v1_0'

/** Documented Chinese speaker `zf_xiaobei` in kokoro-multi-lang-v1_0. */
export const sherpaKokoroDefaultVoiceId = 'zf_xiaobei'

/** Fixed sentence the Electron baseline speaks. */
export const sherpaKokoroBaselineText = '今天天气很好，我们开始本地中文语音测试。'

/** Prompt for one real DeepSeek reply that the sentence path speaks. */
export const sherpaKokoroReplyPrompt = '请只用两句中文回答，不要使用工具。第一句说今天适合出门。第二句说记得带伞。'

/** Thread count from the sherpa-onnx Kokoro Chinese example. */
export const sherpaKokoroNumThreads = 2

export interface SherpaKokoroVoice {
  id: string
  speakerId: number
  name: string
  gender: 'female' | 'male'
  languageCode: 'zh-CN'
  languageTitle: string
}

/**
 * Chinese speakers in kokoro-multi-lang-v1_0.
 * Speaker ids come from the sherpa-onnx Kokoro v1.0 speaker map.
 */
export const sherpaKokoroVoices = [
  { id: 'zf_xiaobei', speakerId: 45, name: 'Xiaobei', gender: 'female', languageCode: 'zh-CN', languageTitle: 'Chinese (Mandarin)' },
  { id: 'zf_xiaoni', speakerId: 46, name: 'Xiaoni', gender: 'female', languageCode: 'zh-CN', languageTitle: 'Chinese (Mandarin)' },
  { id: 'zf_xiaoxiao', speakerId: 47, name: 'Xiaoxiao', gender: 'female', languageCode: 'zh-CN', languageTitle: 'Chinese (Mandarin)' },
  { id: 'zf_xiaoyi', speakerId: 48, name: 'Xiaoyi', gender: 'female', languageCode: 'zh-CN', languageTitle: 'Chinese (Mandarin)' },
  { id: 'zm_yunjian', speakerId: 49, name: 'Yunjian', gender: 'male', languageCode: 'zh-CN', languageTitle: 'Chinese (Mandarin)' },
  { id: 'zm_yunxi', speakerId: 50, name: 'Yunxi', gender: 'male', languageCode: 'zh-CN', languageTitle: 'Chinese (Mandarin)' },
  { id: 'zm_yunxia', speakerId: 51, name: 'Yunxia', gender: 'male', languageCode: 'zh-CN', languageTitle: 'Chinese (Mandarin)' },
  { id: 'zm_yunyang', speakerId: 52, name: 'Yunyang', gender: 'male', languageCode: 'zh-CN', languageTitle: 'Chinese (Mandarin)' },
] as const satisfies readonly SherpaKokoroVoice[]

/** Returns one catalog voice, or `undefined` when the id is not a Chinese Kokoro speaker. */
export function sherpaKokoroVoiceById(voiceId: string): SherpaKokoroVoice | undefined {
  return sherpaKokoroVoices.find(voice => voice.id === voiceId)
}

/**
 * Real-time factor for one whole-utterance synthesis.
 *
 * @example
 * speechRealTimeFactor(500, 1000)
 * // => 0.5
 */
export function speechRealTimeFactor(synthesisMs: number, audioDurationMs: number): number {
  if (!Number.isFinite(synthesisMs) || synthesisMs < 0)
    throw new Error('Synthesis time must be a non-negative number.')
  if (!Number.isFinite(audioDurationMs) || audioDurationMs <= 0)
    throw new Error('Audio duration must be greater than zero.')
  return synthesisMs / audioDurationMs
}

export const sherpaKokoroJobSchema = object({
  jobId: pipe(string(), minLength(1), maxLength(160)),
  turnId: optional(pipe(string(), minLength(1), maxLength(160))),
})

export const sherpaKokoroSynthesizeRequestSchema = object({
  text: pipe(string(), minLength(1), maxLength(2000)),
  voiceId: pipe(string(), minLength(1), maxLength(64)),
  speed: optional(pipe(number(), minValue(0.5), maxValue(2))),
  job: optional(sherpaKokoroJobSchema),
})

export interface SherpaKokoroProcessSample {
  rssBytes: number
  heapUsedBytes: number
  cpuUserMicros: number
  cpuSystemMicros: number
}

export interface SherpaKokoroSynthesisMetrics {
  modelId: string
  voiceId: string
  speakerId: number
  text: string
  loadMs: number
  synthesisMs: number
  audioDurationMs: number
  rtf: number
  sampleRate: number
  wavBytes: number
  numThreads: number
  process: SherpaKokoroProcessSample
}

export interface SherpaKokoroSynthesisResult {
  wavBase64: string
  metrics: SherpaKokoroSynthesisMetrics
}

export interface SherpaKokoroLoadResult {
  loadMs: number
  modelId: string
  process: SherpaKokoroProcessSample
}

export interface SherpaKokoroPlaybackReport {
  played: boolean
  decodeMs: number
  playbackMs: number
  decodedDurationMs: number
  error?: string
}

export interface SherpaKokoroBaselinePayload {
  wavBase64: string | null
  metrics: SherpaKokoroSynthesisMetrics | null
  error: string | null
}

export interface SherpaKokoroReplyPlayback {
  audioItemId: string
  played: boolean
  decodeMs: number
  queueWaitMs: number
  playbackMs: number
  decodedDurationMs: number
  error: string | null
}

export interface SherpaKokoroReplyJob {
  jobId: string
  turnId: string | null
  text: string
  synthesis: SherpaKokoroSynthesisMetrics
  synthesisFinishedAt: string
  playback: SherpaKokoroReplyPlayback | null
}

export interface SherpaKokoroReplyReport {
  recordedAt: string
  prompt: string | null
  assistantText: string | null
  error: string | null
  jobs: SherpaKokoroReplyJob[]
}

export interface SherpaKokoroReplyTurn {
  prompt: string
  assistantText: string
  error: string | null
}

export interface SherpaKokoroReplyPlaybackInput {
  jobId: string
  audioItemId: string
  played: boolean
  decodeMs: number
  playbackMs: number
  decodedDurationMs: number
  startedAt: number
  error?: string
}

export const electronSherpaKokoroSynthesize = defineInvokeEventa<
  SherpaKokoroSynthesisResult,
  { text: string, voiceId: string, speed?: number, job?: { jobId: string, turnId?: string } }
>('eventa:invoke:velis:sherpa-kokoro:synthesize')

export const electronSherpaKokoroEnsureLoaded = defineInvokeEventa<SherpaKokoroLoadResult>(
  'eventa:invoke:velis:sherpa-kokoro:ensure-loaded',
)

export const electronSherpaKokoroBaselineEnabled = defineInvokeEventa<{ enabled: boolean }>(
  'eventa:invoke:velis:sherpa-kokoro:baseline-enabled',
)

export const electronSherpaKokoroTakeBaseline = defineInvokeEventa<SherpaKokoroBaselinePayload | null>(
  'eventa:invoke:velis:sherpa-kokoro:take-baseline',
)

export const electronSherpaKokoroReportPlayback = defineInvokeEventa<void, SherpaKokoroPlaybackReport>(
  'eventa:invoke:velis:sherpa-kokoro:report-playback',
)

export const electronSherpaKokoroReplyEnabled = defineInvokeEventa<{ enabled: boolean }>(
  'eventa:invoke:velis:sherpa-kokoro:reply-enabled',
)

export const electronSherpaKokoroReportReplyTurn = defineInvokeEventa<void, SherpaKokoroReplyTurn>(
  'eventa:invoke:velis:sherpa-kokoro:report-reply-turn',
)

export const electronSherpaKokoroReportReplyPlayback = defineInvokeEventa<void, SherpaKokoroReplyPlaybackInput>(
  'eventa:invoke:velis:sherpa-kokoro:report-reply-playback',
)
